import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ar from './ar';
import en from './en';

/**
 * Lightweight i18n layer.
 *
 * Source strings are written in English directly in the components and wrapped
 * with t('...'). When the UI language is Arabic, the English string is looked up
 * in the Arabic dictionary (src/i18n/ar/*). Missing entries fall back to English,
 * so a forgotten translation never breaks the UI.
 *
 * Interpolation: t('Hello {name}', { name }) -> placeholders in braces.
 * Plurals: a dictionary value can be an object keyed by Intl.PluralRules
 * categories (zero, one, two, few, many, other); the category is picked from
 * vars.count.
 */

export const LANGUAGES = {
  en: { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  ar: { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
};

const STORAGE_KEY = 'ui_language';
const DICTIONARIES = { ar, en };

/**
 * Pick the interface language from the browser's ordered preference list
 * (e.g. ['fr-FR', 'ar-MA', 'en'] -> 'ar'): the first of Arabic / English wins.
 * Languages we don't support (French, ...) are skipped; English is the fallback.
 */
export const detectBrowserLanguage = () => {
  const preferred = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || '']
  ).map((l) => String(l).toLowerCase());
  for (const l of preferred) {
    const base = l.split('-')[0];
    if (LANGUAGES[base]) return base;
  }
  return 'en';
};

const detectInitialLanguage = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('lang');
    if (fromUrl && LANGUAGES[fromUrl]) {
      localStorage.setItem(STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGES[stored]) return stored;
  } catch (e) {
    // storage unavailable
  }
  return detectBrowserLanguage();
};

let currentLang = detectInitialLanguage();

const pluralRulesCache = {};
const pluralCategory = (lang, count) => {
  if (!pluralRulesCache[lang]) pluralRulesCache[lang] = new Intl.PluralRules(lang);
  return pluralRulesCache[lang].select(count);
};

const interpolate = (str, vars) => {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (match, key) =>
    vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : match
  );
};

/**
 * Translate outside of React render (event handlers, helpers, constants built at call time).
 * Inside components prefer the `t` returned by useI18n() so the component re-renders on change.
 */
export const translate = (text, vars, lang = currentLang) => {
  if (text === null || text === undefined) return text;
  const dict = DICTIONARIES[lang];
  let entry = dict ? dict[text] : undefined;

  if (entry && typeof entry === 'object') {
    const count = vars && typeof vars.count === 'number' ? vars.count : 0;
    entry = entry[pluralCategory(lang, count)] ?? entry.other;
  }

  if (typeof entry !== 'string') {
    if (process.env.NODE_ENV === 'development' && lang !== 'en' && typeof text === 'string' && text.trim()) {
      missingKeys.add(text);
    }
    entry = text;
  }
  return interpolate(entry, vars);
};

// Dev helper: window.__missingTranslations() lists strings seen without an Arabic entry
const missingKeys = new Set();
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.__missingTranslations = () => Array.from(missingKeys);
}

/**
 * Render a translated string that contains simple inline tags, e.g.
 *   rich(t('Page <b>{page}</b> of {total}', { page, total }), { b: (c) => <strong>{c}</strong> })
 * Tags cannot be nested. Unknown tags are rendered as plain text content.
 */
export const rich = (str, renderers = {}) => {
  if (typeof str !== 'string') return str;
  const parts = [];
  const re = /<(\w+)>(.*?)<\/\1>/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = re.exec(str)) !== null) {
    if (match.index > last) parts.push(str.slice(last, match.index));
    const render = renderers[match[1]];
    parts.push(
      <React.Fragment key={key++}>{render ? render(match[2]) : match[2]}</React.Fragment>
    );
    last = re.lastIndex;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts;
};

export const getLanguage = () => currentLang;
export const isRTL = () => LANGUAGES[currentLang].dir === 'rtl';

// Locale used for dates / numbers. Arabic month names with Western digits (as used in Morocco).
export const getLocale = (lang = currentLang) => (lang === 'ar' ? 'ar-u-nu-latn' : 'en-US');

export const formatDate = (date, options, lang = currentLang) => {
  try {
    return new Date(date).toLocaleDateString(getLocale(lang), options);
  } catch (e) {
    return '';
  }
};

// Localized country name from an ISO code (falls back to the given English name)
const regionNamesCache = {};
export const countryName = (iso, fallback, lang = currentLang) => {
  if (lang === 'en' || !iso) return fallback;
  try {
    if (!regionNamesCache[lang]) regionNamesCache[lang] = new Intl.DisplayNames([lang], { type: 'region' });
    return regionNamesCache[lang].of(iso) || fallback;
  } catch (e) {
    return fallback;
  }
};

const applyDocumentLanguage = (lang) => {
  const html = document.documentElement;
  html.setAttribute('lang', lang);
  html.setAttribute('dir', LANGUAGES[lang].dir);
};

applyDocumentLanguage(currentLang);

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(currentLang);

  const setLang = useCallback((next) => {
    if (!LANGUAGES[next]) return;
    currentLang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore
    }
    applyDocumentLanguage(next);
    setLangState(next);
  }, []);

  useEffect(() => {
    applyDocumentLanguage(lang);
  }, [lang]);

  const value = useMemo(() => {
    const t = (text, vars) => translate(text, vars, lang);
    return {
      lang,
      dir: LANGUAGES[lang].dir,
      isRTL: LANGUAGES[lang].dir === 'rtl',
      locale: getLocale(lang),
      setLang,
      toggleLang: () => setLang(lang === 'ar' ? 'en' : 'ar'),
      t,
      formatDate: (date, options) => formatDate(date, options, lang),
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  // Fallback when rendered outside the provider (tests, isolated components)
  return {
    lang: currentLang,
    dir: LANGUAGES[currentLang].dir,
    isRTL: isRTL(),
    locale: getLocale(),
    setLang: () => {},
    toggleLang: () => {},
    t: (text, vars) => translate(text, vars),
    formatDate: (date, options) => formatDate(date, options),
  };
};
